/**
 * User 应用默认主题配置
 * 青色(Teal)主题 - iPad 友好设计
 */

import { createTokens, type DesignTokens, type ThemeConfig } from '@ifa/ui';

/**
 * User 应用的品牌色 - 青色系
 */
const userBrandColors = {
  primary: '#08c4c8',        // 青色
  primaryHover: '#06a09a',   // 深青色
  primaryActive: '#058a85',  // 更深青色
  primaryDisabled: '#a7e8ea', // 浅青色
};

/**
 * User 应用的设计令牌
 */
export const userTokens: DesignTokens = createTokens({
  colors: {
    // 品牌色 - 覆盖默认的蓝色为青色
    primary: userBrandColors.primary,
    primaryHover: userBrandColors.primaryHover,
    primaryActive: userBrandColors.primaryActive,
    primaryDisabled: userBrandColors.primaryDisabled,

    // 背景色 - 适配 iPad 浅色主题
    background: {
      base: '#f5f7fa',      // 稍微深一点的灰色
      elevated: '#ffffff',
      hover: '#f0f2f5',
      active: '#e7ecf3',
      overlay: 'rgba(0, 0, 0, 0.45)',
      disabled: '#f8fafc',
    },

    // 边框色 - 使用指定的灰色
    border: {
      base: '#CBD5E1',      // 修改为指定的边框色
      light: '#f1f5f9',
      dark: '#cbd5e1',
      focus: userBrandColors.primary, // 聚焦时使用青色
    },

    // 文本色 - 使用指定的深灰色
    text: {
      primary: '#23262C',   // 修改为指定的文本色
      secondary: '#64748b',
      tertiary: '#94a3b8',
      disabled: '#cbd5e1',
    },
  },

  // 字体 - iPad 优化的字体大小，基础字号改为 16px
  typography: {
    fontFamily: {
      base: 'NotoSans, "Noto Sans"',
      heading: 'NotoSans, "Noto Sans"',
      code: `'SF Mono', 'Monaco', 'Consolas', 'Liberation Mono', 'Courier New', monospace`,
    },
    fontSize: {
      xs: '12px',
      sm: '14px',
      base: '16px',   // iPad 基础字体，也是 Input 的字号
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '32px',
      '4xl': '40px',
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.2,
      base: 1.5,
      relaxed: 1.75,
      loose: 2,
    },
  },

  // 圆角 - 修改为 6px
  borderRadius: {
    none: '0',
    sm: '4px',
    base: '6px',   // 修改为指定的圆角（Input、Button 等使用）
    md: '8px',
    lg: '12px',    // 卡片圆角
    xl: '16px',
    '2xl': '24px',
    full: '9999px',
  },
});

/**
 * User 应用的完整主题配置
 */
export const userTheme: ThemeConfig = {
  name: 'User App - Teal Theme',
  description: 'iPad 优化的青色主题，为 User 应用定制',
  tokens: userTokens,
};

/**
 * User 应用的默认主题（导出为默认）
 */
export default userTheme;
